import { useCallback, useEffect, useState } from 'react'
import { Contact, Plus, Trash2 } from 'lucide-react'

import { CustomerPackagesTab } from '@/components/masters/CustomerPackagesTab'
import { CustomerVisitHistoryTab } from '@/components/masters/CustomerVisitHistoryTab'
import { MasterFormModal } from '@/components/masters/MasterFormModal'
import { MasterServerTable } from '@/components/masters/MasterServerTable'
import { Button } from '@/components/ui/button'
import { useBranch } from '@/contexts/BranchContext'
import {
  createCustomer,
  CUSTOMER_GENDER_OPTIONS,
  deleteCustomer,
  fetchCustomersList,
  fetchCustomersPage,
  updateCustomer,
} from '@/lib/api/customers'
import { fetchStylists } from '@/lib/api/stylists'
import {
  DEFAULT_GCC_COUNTRY_CODE,
  detectCountryFromE164,
  e164ToLocalDigits,
  formatE164Display,
  formatLocalPhoneInput,
  GCC_COUNTRY_CODES,
  PHONE_LOCAL_PLACEHOLDER,
  toE164Phone,
} from '@/lib/phone'
import { cn } from '@/lib/utils'

const CUSTOMER_TABS = [
  { id: 'profile', label: 'Profile' },
  { id: 'visits', label: 'Visit history' },
  { id: 'packages', label: 'Packages' },
]

function emptyForm() {
  return {
    name: '',
    phoneCountry: DEFAULT_GCC_COUNTRY_CODE,
    phoneLocal: '',
    gender: 'unknown',
    email: '',
    dob: '',
    allergies: '',
    notes: '',
    preferred_stylist_id: '',
    marketing_consent: false,
    blacklist: false,
  }
}

/**
 * @param {import('@/types/api').Customer} customer
 */
function customerToForm(customer) {
  const phoneCountry = detectCountryFromE164(customer.phone ?? '')
  return {
    name: customer.name ?? '',
    phoneCountry,
    phoneLocal: formatLocalPhoneInput(
      e164ToLocalDigits(customer.phone ?? '', phoneCountry),
      phoneCountry,
    ),
    gender: customer.gender ?? 'unknown',
    email: customer.email ?? '',
    dob: customer.dob ?? '',
    allergies: customer.allergies ?? '',
    notes: customer.notes ?? '',
    preferred_stylist_id: customer.preferred_stylist_id ?? '',
    marketing_consent: Boolean(customer.marketing_consent),
    blacklist: Boolean(customer.blacklist),
  }
}

function getFieldError(error, field) {
  return error?.response?.data?.errors?.[field]?.[0] ?? null
}

const EXPORT_COLUMNS = [
  { key: 'name', label: 'Name', getValue: (row) => row.name },
  { key: 'phone', label: 'Phone', getValue: (row) => row.phone },
  { key: 'gender', label: 'Gender', getValue: (row) => row.gender ?? '' },
  { key: 'email', label: 'Email', getValue: (row) => row.email ?? '' },
  { key: 'allergies', label: 'Allergies', getValue: (row) => row.allergies ?? '' },
  {
    key: 'marketing_consent',
    label: 'Marketing consent',
    getValue: (row) => (row.marketing_consent ? 'yes' : 'no'),
  },
  { key: 'blacklist', label: 'Blacklisted', getValue: (row) => (row.blacklist ? 'yes' : 'no') },
]

export function CustomersPage() {
  const { activeBranchId } = useBranch()
  const [stylists, setStylists] = useState([])
  const [modalOpen, setModalOpen] = useState(false)
  const [creating, setCreating] = useState(false)
  const [selectedId, setSelectedId] = useState(null)
  const [activeTab, setActiveTab] = useState('profile')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [form, setForm] = useState(emptyForm())
  const [reloadKey, setReloadKey] = useState(0)

  const fetchPage = useCallback((params) => fetchCustomersPage(params), [])

  useEffect(() => {
    let active = true

    async function loadStylists() {
      try {
        const items = await fetchStylists()
        if (active) setStylists(items)
      } catch {
        if (active) setStylists([])
      }
    }

    loadStylists()
    return () => {
      active = false
    }
  }, [activeBranchId])

  function openCreate() {
    setCreating(true)
    setSelectedId(null)
    setForm(emptyForm())
    setActiveTab('profile')
    setError('')
    setSuccess('')
    setModalOpen(true)
  }

  /**
   * @param {import('@/types/api').Customer} customer
   */
  function openEdit(customer) {
    setCreating(false)
    setSelectedId(customer.id)
    setForm(customerToForm(customer))
    setActiveTab('profile')
    setError('')
    setSuccess('')
    setModalOpen(true)
  }

  function closeModal() {
    if (submitting) return
    setModalOpen(false)
  }

  function updateField(field, value) {
    setForm((current) => ({ ...current, [field]: value }))
    setSuccess('')
  }

  function updatePhoneLocal(value) {
    const formatted = formatLocalPhoneInput(value, form.phoneCountry)
    updateField('phoneLocal', formatted)
  }

  function updatePhoneCountry(countryCode) {
    setForm((current) => ({
      ...current,
      phoneCountry: countryCode,
      phoneLocal: formatLocalPhoneInput(current.phoneLocal, countryCode),
    }))
    setSuccess('')
  }

  async function handleSubmit(event) {
    event.preventDefault()
    setSubmitting(true)
    setError('')
    setSuccess('')

    const phone = toE164Phone(form.phoneLocal, form.phoneCountry)
    const payload = {
      name: form.name,
      phone,
      gender: form.gender,
      email: form.email || null,
      dob: form.dob || null,
      allergies: form.allergies || null,
      notes: form.notes || null,
      preferred_stylist_id: form.preferred_stylist_id
        ? Number(form.preferred_stylist_id)
        : null,
      marketing_consent: form.marketing_consent,
    }

    if (!creating) {
      payload.blacklist = form.blacklist
    }

    try {
      if (creating) {
        await createCustomer(payload)
        setSuccess('Customer created.')
      } else if (selectedId) {
        await updateCustomer(selectedId, payload)
        setSuccess('Customer saved.')
      }
      setReloadKey((current) => current + 1)
      setModalOpen(false)
    } catch (err) {
      const message =
        getFieldError(err, 'phone') ??
        getFieldError(err, 'name') ??
        getFieldError(err, 'email') ??
        err?.response?.data?.message ??
        'Unable to save customer.'
      setError(message)
    } finally {
      setSubmitting(false)
    }
  }

  async function handleDelete() {
    if (!selectedId || creating) return

    const guestName = form.name.trim() || 'this guest'
    if (
      !window.confirm(
        `Delete ${guestName}? The profile will be removed from guest lists. Past sales and visit history are kept.`,
      )
    ) {
      return
    }

    setSubmitting(true)
    setError('')
    setSuccess('')

    try {
      await deleteCustomer(selectedId)
      setReloadKey((current) => current + 1)
      setSuccess('Customer deleted.')
      setModalOpen(false)
    } catch (err) {
      setError(err?.response?.data?.message ?? 'Unable to delete customer.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="mx-auto max-w-[1440px] px-4 py-8 lg:px-8">
      <div className="mb-6 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/10 text-primary">
            <Contact className="h-5 w-5" aria-hidden />
          </div>
          <div>
            <h1 className="text-xl font-semibold tracking-tight">Customers</h1>
            <p className="text-sm text-muted-foreground">
              Guest profiles with GCC phone format, allergies, and marketing consent.
            </p>
          </div>
        </div>
        <Button type="button" variant="outline" onClick={openCreate}>
          <Plus className="h-4 w-4" />
          New customer
        </Button>
      </div>

      {success && !modalOpen ? (
        <p className="mb-4 text-sm text-primary" role="status">
          {success}
        </p>
      ) : null}

      {error && !modalOpen ? (
        <p className="mb-4 text-sm text-destructive" role="alert">
          {error}
        </p>
      ) : null}

      <MasterServerTable
        key={activeBranchId}
        fetchPage={fetchPage}
        reloadKey={reloadKey}
        getRowKey={(customer) => customer.id}
        onEdit={openEdit}
        emptyLabel="No customers yet."
        searchPlaceholder="Search customers…"
        exportColumns={EXPORT_COLUMNS}
        exportFilename="customers.csv"
        fetchAllForExport={(params) => fetchCustomersList(params)}
        columns={[
          { key: 'name', label: 'Name', sortable: true, render: (row) => row.name },
          {
            key: 'phone',
            label: 'Phone',
            sortable: true,
            render: (row) => formatE164Display(row.phone),
          },
          {
            key: 'gender',
            label: 'Gender',
            render: (row) =>
              CUSTOMER_GENDER_OPTIONS.find((option) => option.value === row.gender)?.label ??
              row.gender,
          },
          { key: 'email', label: 'Email', render: (row) => row.email || '—' },
          {
            key: 'blacklist',
            label: 'Status',
            render: (row) => (row.blacklist ? 'Blacklisted' : 'Active'),
          },
        ]}
      />

      <MasterFormModal
        open={modalOpen}
        onClose={closeModal}
        size="2xl"
        title={creating ? 'New customer' : 'Edit customer'}
        description={
          creating
            ? 'Create a guest profile for bookings and POS.'
            : 'Update profile details or manage packages.'
        }
      >
        {!creating ? (
          <div className="mb-5 flex flex-wrap gap-2">
            {CUSTOMER_TABS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={cn(
                  'rounded-xl px-3 py-1.5 text-sm font-medium transition-colors',
                  activeTab === tab.id
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted/70 text-foreground hover:bg-muted',
                )}
              >
                {tab.label}
              </button>
            ))}
          </div>
        ) : null}

        {activeTab === 'packages' && !creating && selectedId ? (
          <CustomerPackagesTab customerId={selectedId} />
        ) : activeTab === 'visits' && !creating && selectedId ? (
          <CustomerVisitHistoryTab customerId={selectedId} />
        ) : (
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label htmlFor="customer-name" className="mb-1.5 block text-sm font-medium">
                  Name
                </label>
                <input
                  id="customer-name"
                  type="text"
                  value={form.name}
                  onChange={(e) => updateField('name', e.target.value)}
                  className={cn(
                    'h-11 w-full rounded-xl border border-border/80 bg-card px-4 text-sm',
                    'focus:outline-none focus:ring-2 focus:ring-ring',
                  )}
                  required
                />
              </div>
              <div>
                <label htmlFor="customer-gender" className="mb-1.5 block text-sm font-medium">
                  Gender
                </label>
                <select
                  id="customer-gender"
                  value={form.gender}
                  onChange={(e) => updateField('gender', e.target.value)}
                  className={cn(
                    'h-11 w-full rounded-xl border border-border/80 bg-card px-4 text-sm',
                    'focus:outline-none focus:ring-2 focus:ring-ring',
                  )}
                >
                  {CUSTOMER_GENDER_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="mb-1.5 block text-sm font-medium">Phone (GCC)</label>
              <div className="grid gap-2 sm:grid-cols-[140px_minmax(0,1fr)]">
                <select
                  value={form.phoneCountry}
                  onChange={(e) => updatePhoneCountry(e.target.value)}
                  className={cn(
                    'h-11 w-full rounded-xl border border-border/80 bg-card px-4 text-sm',
                    'focus:outline-none focus:ring-2 focus:ring-ring',
                  )}
                >
                  {GCC_COUNTRY_CODES.map((country) => (
                    <option key={country.code} value={country.code}>
                      {country.label} {country.code}
                    </option>
                  ))}
                </select>
                <input
                  id="customer-phone"
                  type="tel"
                  value={form.phoneLocal}
                  onChange={(e) => updatePhoneLocal(e.target.value)}
                  placeholder={PHONE_LOCAL_PLACEHOLDER}
                  className={cn(
                    'h-11 w-full rounded-xl border border-border/80 bg-card px-4 text-sm',
                    'focus:outline-none focus:ring-2 focus:ring-ring',
                  )}
                  required
                />
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label htmlFor="customer-email" className="mb-1.5 block text-sm font-medium">
                  Email
                </label>
                <input
                  id="customer-email"
                  type="email"
                  value={form.email}
                  onChange={(e) => updateField('email', e.target.value)}
                  className={cn(
                    'h-11 w-full rounded-xl border border-border/80 bg-card px-4 text-sm',
                    'focus:outline-none focus:ring-2 focus:ring-ring',
                  )}
                />
              </div>
              <div>
                <label htmlFor="customer-dob" className="mb-1.5 block text-sm font-medium">
                  Date of birth
                </label>
                <input
                  id="customer-dob"
                  type="date"
                  value={form.dob}
                  onChange={(e) => updateField('dob', e.target.value)}
                  className={cn(
                    'h-11 w-full rounded-xl border border-border/80 bg-card px-4 text-sm',
                    'focus:outline-none focus:ring-2 focus:ring-ring',
                  )}
                />
              </div>
            </div>

            <div>
              <label htmlFor="customer-allergies" className="mb-1.5 block text-sm font-medium">
                Allergies & sensitivities
              </label>
              <textarea
                id="customer-allergies"
                value={form.allergies}
                onChange={(e) => updateField('allergies', e.target.value)}
                rows={3}
                className={cn(
                  'w-full rounded-xl border border-border/80 bg-card px-4 py-3 text-sm',
                  'focus:outline-none focus:ring-2 focus:ring-ring',
                )}
                placeholder="e.g. PPD dye, latex, nut oils…"
              />
            </div>

            <div>
              <label htmlFor="customer-stylist" className="mb-1.5 block text-sm font-medium">
                Preferred stylist
              </label>
              <select
                id="customer-stylist"
                value={form.preferred_stylist_id}
                onChange={(e) => updateField('preferred_stylist_id', e.target.value)}
                className={cn(
                  'h-11 w-full rounded-xl border border-border/80 bg-card px-4 text-sm',
                  'focus:outline-none focus:ring-2 focus:ring-ring',
                )}
              >
                <option value="">None</option>
                {stylists.map((stylist) => (
                  <option key={stylist.id} value={stylist.id}>
                    {stylist.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="customer-notes" className="mb-1.5 block text-sm font-medium">
                Notes
              </label>
              <textarea
                id="customer-notes"
                value={form.notes}
                onChange={(e) => updateField('notes', e.target.value)}
                rows={3}
                className={cn(
                  'w-full rounded-xl border border-border/80 bg-card px-4 py-3 text-sm',
                  'focus:outline-none focus:ring-2 focus:ring-ring',
                )}
              />
            </div>

            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.marketing_consent}
                onChange={(e) => updateField('marketing_consent', e.target.checked)}
                className="rounded border-border"
              />
              Marketing consent (WhatsApp / SMS / email)
            </label>

            {!creating ? (
              <label className="flex items-center gap-2 text-sm text-destructive">
                <input
                  type="checkbox"
                  checked={form.blacklist}
                  onChange={(e) => updateField('blacklist', e.target.checked)}
                  className="rounded border-border"
                />
                Blacklisted — block bookings and POS
              </label>
            ) : null}

            {error ? (
              <p className="text-sm text-destructive" role="alert">
                {error}
              </p>
            ) : null}

            <div className="flex flex-wrap justify-between gap-2">
              {!creating && selectedId ? (
                <Button
                  type="button"
                  variant="outline"
                  disabled={submitting}
                  onClick={handleDelete}
                  className="text-destructive hover:text-destructive"
                >
                  <Trash2 className="h-4 w-4" />
                  Delete guest
                </Button>
              ) : (
                <span />
              )}
              <div className="flex gap-2">
                <Button type="button" variant="outline" onClick={closeModal} disabled={submitting}>
                  Cancel
                </Button>
                <Button type="submit" disabled={submitting}>
                  {submitting ? 'Saving…' : creating ? 'Create customer' : 'Save customer'}
                </Button>
              </div>
            </div>
          </form>
        )}

        {!creating && selectedId && activeTab !== 'profile' ? (
          <div className="mt-5 flex flex-wrap justify-between gap-2 border-t border-border/50 pt-4">
            <Button
              type="button"
              variant="outline"
              disabled={submitting}
              onClick={handleDelete}
              className="text-destructive hover:text-destructive"
            >
              <Trash2 className="h-4 w-4" />
              Delete guest
            </Button>
            <Button type="button" variant="outline" onClick={closeModal} disabled={submitting}>
              Close
            </Button>
          </div>
        ) : null}

        {activeTab !== 'profile' && !creating && error ? (
          <p className="mt-3 text-sm text-destructive" role="alert">
            {error}
          </p>
        ) : null}
      </MasterFormModal>
    </div>
  )
}
